import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BackToInvoicesLink } from "@/components/invoice/BackToInvoicesLink";
import { InvoiceForm } from "@/components/invoice/InvoiceForm";
import { PageHeader } from "@/components/layout/PageHeader";
import { ErrorPanel, LoadingPanel } from "@/components/ui/Loading";
import { useToast } from "@/components/ui/Toast";
import type { BootstrapPayload, InvoiceRecord } from "@/domain/types";
import {
  createInvoiceFormDefaults,
  formToInvoiceInput,
  invoiceToForm,
  type InvoiceFormValues,
} from "@/features/invoices/invoice-form";
import { apiRequest } from "@/lib/api";

export function InvoiceEditorPage() {
  const { id = "" } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [bootstrap, setBootstrap] = useState<BootstrapPayload | null>(null);
  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      apiRequest<BootstrapPayload>("/api/admin/bootstrap"),
      editing
        ? apiRequest<InvoiceRecord>(`/api/admin/invoices/${id}`)
        : Promise.resolve(null),
    ])
      .then(([nextBootstrap, nextInvoice]) => {
        if (cancelled) return;
        setBootstrap(nextBootstrap);
        setInvoice(nextInvoice);
      })
      .catch((caught) => {
        if (cancelled) return;
        setError(
          caught instanceof Error
            ? caught
            : new Error("Could not load invoice editor."),
        );
      });

    return () => {
      cancelled = true;
    };
  }, [editing, id]);

  if (error) return <ErrorPanel error={error} />;
  if (!bootstrap || (editing && !invoice)) {
    return <LoadingPanel label="Loading invoice editor…" />;
  }

  const initialValues = editing
    ? invoiceToForm(invoice!)
    : createInvoiceFormDefaults(
        bootstrap.company,
        undefined,
        bootstrap.workspaceSettings.defaultCurrency,
        bootstrap.workspaceSettings.paymentQrAssets,
      );

  const submit = async (values: InvoiceFormValues) => {
    try {
      const input = formToInvoiceInput(values);
      const saved = await apiRequest<InvoiceRecord>(
        editing
          ? `/api/admin/invoices/${id}`
          : "/api/admin/invoices",
        {
          method: editing ? "PUT" : "POST",
          body: input,
        },
      );

      showToast(
        editing ? "Invoice updated." : "Invoice created.",
      );
      navigate(`/app/invoices/${saved.id}`);
    } catch (caught) {
      showToast(
        caught instanceof Error
          ? caught.message
          : "Could not save invoice.",
        "error",
      );
    }
  };

  const detailPath = editing
    ? `/app/invoices/${id}`
    : "/app";

  return (
    <>
      <BackToInvoicesLink
        label={editing ? "Back to invoice" : "Back to invoices"}
        to={detailPath}
      />
      <PageHeader
        eyebrow={editing ? "Edit invoice" : "Invoice builder"}
        title={editing ? `Edit ${invoice!.invoiceNumber}` : "Create a new invoice"}
        description="Use different currencies, rates, billing methods, cycles, and due agreements for every client or invoice."
      />
      <InvoiceForm
        initialValues={initialValues}
        clients={bootstrap.clients}
        paymentQrAssets={bootstrap.workspaceSettings.paymentQrAssets}
        submitLabel={editing ? "Save invoice" : "Create invoice"}
        cancelTo={detailPath}
        onSubmit={submit}
      />
    </>
  );
}
